import { IDENTITY_PLACEHOLDERS, placeholdersIn, SHOPPER_PLACEHOLDERS } from '../connectorConfig';

const PLACEHOLDER = /\{\{\s*([^}\s]+)\s*\}\}/g;

// Two kinds a CS user tells apart: what the agent passes in, and what is known about the shopper.
export const placeholderKind = (name, argNames) => {
  const argMatch = /^args\.([^.]+)$/.exec(name);
  if (argMatch) return argNames.includes(argMatch[1]) ? 'args' : 'unknown';
  if (IDENTITY_PLACEHOLDERS.includes(name) || SHOPPER_PLACEHOLDERS.includes(name)) return 'shopper';
  return 'unknown';
};

export const placeholderOptions = (argNames) => [
  ...argNames.map((name) => `args.${name}`),
  ...IDENTITY_PLACEHOLDERS,
  ...SHOPPER_PLACEHOLDERS,
];

// The text split into plain runs and placeholders, for the coloured view under an input.
export const placeholderTokens = (text) => {
  const value = String(text ?? '');
  const tokens = [];
  let last = 0;
  for (const match of value.matchAll(PLACEHOLDER)) {
    if (match.index > last) tokens.push({ text: value.slice(last, match.index) });
    tokens.push({ name: match[1] });
    last = match.index + match[0].length;
  }
  if (last < value.length) tokens.push({ text: value.slice(last) });
  return tokens;
};

// An open `{{` before the caret with no `}` or space after it: what the reader is typing a name for.
export const placeholderQuery = (text, caret) => {
  const before = String(text ?? '').slice(0, caret);
  const start = before.lastIndexOf('{{');
  if (start === -1) return null;
  const typed = before.slice(start + 2);
  return /[}\s]/.test(typed) ? null : { start, typed };
};

export const insertPlaceholder = (text, caret, query, name) => {
  const after = text.slice(caret).replace(/^[^}\s{]*\}\}/, '');
  const inserted = `{{${name}}}`;
  return { text: text.slice(0, query.start) + inserted + after, caret: query.start + inserted.length };
};

export const requestPlaceholders = (form) => [
  ...new Set(
    [form.url, ...form.headerRows.map((row) => row.value), form.method === 'POST' ? form.body : ''].flatMap(
      placeholdersIn
    )
  ),
];

// The argument names a capability's request reads; its own handler fills them on a live call.
export const platformArgs = (form) =>
  requestPlaceholders(form)
    .map((name) => /^args\.([^.]+)$/.exec(name)?.[1])
    .filter(Boolean);

// The shopper-side rows of the arguments table: every identity or shopper field the request reads.
export const shopperInputs = (form) =>
  requestPlaceholders(form).filter(
    (name) => IDENTITY_PLACEHOLDERS.includes(name) || SHOPPER_PLACEHOLDERS.includes(name)
  );

// The probe route takes strings only. An optional argument left blank drops its key so the server
// treats it exactly as a shopper who never raised it; a required one still travels, empty, so the
// server's own validation can name it.
export const probeArgsBody = (values, tool) => {
  const args = tool?.args || [];

  return args.reduce((body, arg) => {
    const raw = values[arg.name];

    // A Switch has no blank state, so a boolean is never blank: untouched means false, which is
    // the value a shopper who never raised it would get. Read as blank instead, a required
    // boolean whose real answer is false could only be probed as '' and would fail the gate.
    if (arg.type === 'boolean') {
      body[arg.name] = String(!!raw);
      return body;
    }

    const trimmed = typeof raw === 'string' ? raw.trim() : raw;
    const blank = trimmed === undefined || trimmed === null || trimmed === '';

    if (arg.required || !blank) body[arg.name] = blank ? '' : String(trimmed);
    return body;
  }, {});
};

const coordinate = (value) => {
  const number = Number(value);
  return value.trim() !== '' && Number.isFinite(number) ? number : value.trim();
};

// The test inputs of C7 (and of /:id/probe), built from the values keyed by placeholder name.
// A capability (tool null) declares no arguments: every {{args.*}} the request reads travels.
export const probeInputsFor = (form, tool, values) => {
  const argValues = Object.fromEntries(
    Object.entries(values)
      .filter(([name]) => name.startsWith('args.'))
      .map(([name, value]) => [name.slice(5), value])
  );
  const args = tool
    ? probeArgsBody(argValues, tool)
    : probeArgsBody(argValues, { args: platformArgs(form).map((name) => ({ name, type: 'string' })) });
  const identity = {};
  const shopper = {};
  shopperInputs(form).forEach((name) => {
    const value = String(values[name] ?? '').trim();
    if (!value) return;
    const [kind, key] = name.split('.');
    if (kind === 'identity') identity[key] = value;
    else shopper[key] = key === 'city' ? value : coordinate(value);
  });

  const inputs = { identity, args };
  if (Object.keys(shopper).length) inputs.shopper = shopper;
  return inputs;
};

const storageKey = (id) => `connector-test-values:${id || 'new'}`;

// A convenience only: private windows and blocked storage throw, and the workspace starts empty then.
export const readTestValues = (id) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey(id)) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const writeTestValues = (id, values) => {
  try {
    window.localStorage.setItem(storageKey(id), JSON.stringify(values));
  } catch {
    // Not stored; the values still hold for this visit.
  }
};
